import React, { useState } from 'react'

import PropTypes from 'prop-types'

import { useSelector } from 'react-redux'
import { selectIsLoading } from '@selectors'

import {
  Button,
  DARK_GREEN,
  GREEN,
  IconInline,
  Input,
  Paragraph,
  RED_70
} from '@omnilert/components'

import { SAFEWALK_CONTACTS_SUBMIT } from '@actions/types'

import { Formik, Form, FieldArray } from 'formik'
import { SAFEWALK_CONTACTS_FORM_DESCRIPTION, SAFEWALK_OFF_CAMPUS_CONTACTS_FORM_DESCRIPTION } from '@assets/i18n/strings'

import * as Yup from 'yup'

import i18n from 'i18n-js'

import useStyles from './safewalk-contact-form.style'

const SafeWalkContactForm = ({ contacts, onSubmit }) => {
  const classes = useStyles()
  const [countryCode, setCountryCode] = useState('+1')

  const { isOnCampus, isOffHours } = useSelector(state => state.safewalk)
  const loading = useSelector(selectIsLoading)
  const isSubmittingContacts = loading.some(({ loadingType }) => loadingType === SAFEWALK_CONTACTS_SUBMIT)

  const initialValues = {
    contacts: contacts.length ? contacts : [{ name: '', number: '', internationalNumber: '' }]
  }

  const contactsSchema = Yup.object().shape({
    contacts: Yup.array()
      .of(
        Yup.object().shape({
          number: Yup.string().required('Phone number required')
        })
      )
      .required('Must have contacts')
  })

  const isOffCampusHours = !isOnCampus || isOffHours
  const formDescription = isOffCampusHours ? i18n.t(SAFEWALK_OFF_CAMPUS_CONTACTS_FORM_DESCRIPTION) : i18n.t(SAFEWALK_CONTACTS_FORM_DESCRIPTION)

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={onSubmit}
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={contactsSchema}
    >
      {({ values, errors, handleSubmit, handleChange, setErrors, setFieldValue }) => (
        <Form
          className={classes.contactForm}
          onSubmit={handleSubmit}
        >
          <FieldArray name='contacts'>
            {({ remove, push }) => (
              <div>
                <Paragraph
                  className={classes.text}
                  color='var(--resource__description__text)'
                  size='md'
                >
                  {formDescription}
                </Paragraph>
                {values.contacts.map((_, index) => (
                  <div
                    key={index}
                    className={classes.field}
                  >
                    <Input
                      autoFormat={false}
                      borderColor='var(--input__border)'
                      className={classes.phoneNumberInput}
                      errorMessage={!!errors?.contacts?.length && !!errors?.contacts[index]?.internationalNumber}
                      name={`contacts.${index}.internationalNumber`}
                      onChange={(e) => {
                        const value = e.target.value
                        if (value.length) {
                          setFieldValue(`contacts.${index}.number`, value.slice(countryCode.length), true)
                        }
                        handleChange(e)
                      }}
                      onChangeCountry={(_, data, event = '', formattedValue) => {
                        setCountryCode(data.dialCode)
                        if (values.contacts[index].number) {
                          setFieldValue(`contacts.${index}.number`, values.contacts[index].number.slice(data.dialCode.length), true)
                          setFieldValue(`contacts.${index}.internationalNumber`, formattedValue, true)
                        }
                      }}
                      pattern='[0-9]{3} [0-9]{3} [0-9]{4}'
                      placeholder='Enter a number'
                      size='md'
                      type='tel'
                      value={values.contacts[index].internationalNumber}
                    />
                    <Button
                      backgroundColor='transparent'
                      className={classes.buttonDelete}
                      hoverBackgroundColor={RED_70}
                      icon={<IconInline
                        className={classes.iconMinus}
                        name='circle-minus'
                        size={28}
                      />}
                      onClick={() => {
                        setErrors({})
                        remove(index)
                      }}
                      size='lg'
                      titleClassName={classes.contactsButtonTitle}
                      type='button'
                      variant='outline'
                    />
                  </div>
                ))}
                <div className={classes.addSubmitContainer}>
                  {values.contacts.length < 3 && <Button
                    backgroundColor={GREEN}
                    className={classes.buttonAdd}
                    color={GREEN}
                    hoverBackgroundColor={DARK_GREEN}
                    hoverColor={DARK_GREEN}
                    icon={<IconInline
                      className={classes.icon}
                      name='circle-plus'
                      size={28}
                    />}
                    onClick={() => {
                      setErrors({})
                      push({ name: '', number: '' })
                    }}
                    size='lg'
                    titleClassName={classes.contactsButtonTitle}
                    type='button'
                  />}
                  <Button
                    className={classes.buttonAdd}
                    isLoading={isSubmittingContacts}
                    size='lg'
                    title='Update contacts'
                    titleClassName={classes.contactsButtonTitle}
                    type='submit'
                    variant='solid'
                  />
                </div>
              </div>
            )}
          </FieldArray>
        </Form>
      )}
    </Formik>
  )
}

SafeWalkContactForm.defaultProps = {
  contacts: [],
  onSubmit: () => { }
}

SafeWalkContactForm.propTypes = {
  contacts: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string })),
  onSubmit: PropTypes.func.isRequired
}

export default SafeWalkContactForm
